/**
 * RevqdemoMetricsPanel — "Next Logical Step" reveal on the JWT page.
 *
 * Reads three tenant-scoped endpoints in parallel using the EDI JWT and
 * surfaces:
 *   a) Total sub-logins (primary token only — sub-user tokens get 403)
 *   b) Total Billing Profile IDs (vendor `clients` — plus linked_npi_count per profile)
 *   c) Total NPIs — assigned vs unassigned
 *
 * Pure read — no mutation. Safe to retry.
 */
import { useState } from 'react';
import { Button } from '../ui/button';
import { Loader2, RefreshCw, AlertTriangle, Users, Building2, Stethoscope, Plus } from 'lucide-react';
import { getSubLogins, getClients, getProviders } from '../../lib/api/ediApi';
import AddNpiDialog from './AddNpiDialog';
import AddBillingProfileDialog from './AddBillingProfileDialog';
import AddSubLoginDialog from './AddSubLoginDialog';

const StatRow = ({ icon: Icon, label, value, testid, sub, action }) => (
  <div className="flex items-center gap-3 px-3 py-2.5 border border-slate-200 rounded-lg bg-white">
    <div className="h-8 w-8 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
      <Icon className="h-4 w-4 text-slate-600" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
    <p
      className="text-xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']"
      data-testid={testid}
    >
      {value}
    </p>
    {action}
  </div>
);

const RevqdemoMetricsPanel = ({ token }) => {
  const [state, setState] = useState({ phase: 'idle', error: null, data: null });
  const [addOpen, setAddOpen] = useState(false);
  const [addProfileOpen, setAddProfileOpen] = useState(false);
  const [addSubLoginOpen, setAddSubLoginOpen] = useState(false);

  const fetchAll = async () => {
    setState({ phase: 'loading', error: null, data: null });
    try {
      const [subRes, cliRes, provRes] = await Promise.all([
        getSubLogins(token),
        getClients(token),
        getProviders(token),
      ]);

      // sub-logins is primary-only; tolerate 403 as "not primary token"
      const subLogins = subRes.ok ? subRes.data?.sub_logins || [] : null;
      const clients = cliRes.ok ? cliRes.data?.clients || [] : null;
      const providers = provRes.ok ? provRes.data?.providers || [] : null;

      // Surface a hard error only when ALL three failed (token bad / vendor down)
      if (subLogins === null && clients === null && providers === null) {
        const msg = subRes.data?.detail || cliRes.data?.detail || provRes.data?.detail || 'Vendor reads failed';
        throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }

      const assigned = providers ? providers.filter((p) => p.client_id).length : 0;
      const unassigned = providers ? providers.filter((p) => !p.client_id).length : 0;

      setState({
        phase: 'done',
        error: null,
        data: {
          subLogins,
          subLoginsForbidden: subRes.status === 403,
          totalSubLogins: subLogins ? subLogins.length : null,
          totalClients: clients ? clients.length : null,
          totalNPIs: providers ? providers.length : null,
          assigned,
          unassigned,
        },
      });
    } catch (err) {
      setState({ phase: 'error', error: err.message || 'Fetch failed', data: null });
    }
  };

  if (state.phase === 'idle') {
    return (
      <Button
        type="button"
        onClick={fetchAll}
        className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-sm gap-2"
        data-testid="revqdemo-next-step"
      >
        Next Logical Step
      </Button>
    );
  }

  return (
    <div className="space-y-3" data-testid="revqdemo-metrics">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          Tenant snapshot
        </p>
        <button
          type="button"
          onClick={fetchAll}
          disabled={state.phase === 'loading'}
          className="text-slate-500 hover:text-slate-800 transition-colors"
          data-testid="revqdemo-metrics-refresh"
          aria-label="Refresh metrics"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${state.phase === 'loading' ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {state.phase === 'loading' && (
        <div className="flex items-center justify-center py-6 text-[11px] text-slate-500" data-testid="revqdemo-metrics-loading">
          <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
          Reading from ehi.ariaone.ai …
        </div>
      )}

      {state.phase === 'error' && (
        <div
          className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-[11px] text-red-700"
          data-testid="revqdemo-metrics-error"
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{state.error}</span>
        </div>
      )}

      {state.phase === 'done' && state.data && (
        <div className="space-y-2">
          <StatRow
            icon={Users}
            label="Sub-logins"
            sub={
              state.data.subLoginsForbidden
                ? 'Primary token required (you are signed in as sub-user)'
                : 'GET /api/edi/sub-logins'
            }
            value={state.data.subLoginsForbidden ? '—' : (state.data.totalSubLogins ?? '—')}
            testid="metric-sub-logins"
            action={
              !state.data.subLoginsForbidden ? (
                <button
                  type="button"
                  onClick={() => setAddSubLoginOpen(true)}
                  className="ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-[11px] font-medium text-slate-700 transition-colors"
                  data-testid="add-sublogin-trigger"
                  aria-label="Create sub-login"
                >
                  <Plus className="h-3 w-3" />
                  Add Sub-login
                </button>
              ) : null
            }
          />
          <StatRow
            icon={Building2}
            label="Billing Profile IDs"
            sub="GET /api/edi/note-to-claim/clients"
            value={state.data.totalClients ?? '—'}
            testid="metric-clients"
            action={
              <button
                type="button"
                onClick={() => setAddProfileOpen(true)}
                className="ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-[11px] font-medium text-slate-700 transition-colors"
                data-testid="add-profile-trigger"
                aria-label="Add Billing Profile"
              >
                <Plus className="h-3 w-3" />
                Add Profile
              </button>
            }
          />
          <StatRow
            icon={Stethoscope}
            label="NPIs (total)"
            sub={`${state.data.assigned} assigned · ${state.data.unassigned} unassigned`}
            value={state.data.totalNPIs ?? '—'}
            testid="metric-npis"
            action={
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-[11px] font-medium text-slate-700 transition-colors"
                data-testid="add-npi-trigger"
              >
                <Plus className="h-3 w-3" />
                Add NPI
              </button>
            }
          />
        </div>
      )}

      {addOpen && (
        <AddNpiDialog
          token={token}
          onClose={() => setAddOpen(false)}
          onAdded={fetchAll}
        />
      )}

      {addProfileOpen && (
        <AddBillingProfileDialog
          token={token}
          onClose={() => setAddProfileOpen(false)}
          onAdded={fetchAll}
        />
      )}

      {addSubLoginOpen && (
        <AddSubLoginDialog
          token={token}
          onClose={() => setAddSubLoginOpen(false)}
          onAdded={fetchAll}
        />
      )}
    </div>
  );
};

export default RevqdemoMetricsPanel;
